import { useCallback, useMemo, useState } from "react";
import Router from "next/router";
import dynamic from "next/dynamic";
import useUser from "@/common/hook/user/useUser";
import useSocket from "@/common/hook/useSocket";
import usePingChat from "@/common/hook/usePingChat";
import UserState from "@/common/models/UserState";

const CallConsultantLivePopup = dynamic(() =>
  import("@/components/common/CallConsultantPopup/CallConsultantLivePopup")
);

const POPUP_CLASSES = {
  znzz: dynamic(() => import("@/components/common/ZNZZPopup")),
  video: CallConsultantLivePopup,
  audio: CallConsultantLivePopup,
  phone: dynamic(() => import("@/components/common/CallConsultantPopup/CallPopup")),
  "add-phone-number": dynamic(() => import("@/components/common/AddPhoneNumberPopup")),
  "no-credit": dynamic(() => import("@/components/common/CallConsultantPopup/CallConsultantPopupNoCredit")),
  callback: dynamic(() => import("@/components/common/CallConsultantPopup/RequestCallbackPopup")),
};

export default function useCallConsultant(onClose) {
  const { isAuthenticated, user = {} } = useUser();
  const socket = useSocket();
  const pingToChat = usePingChat();
  const [activePopup, setActivePopup] = useState(null);

  const call = useCallback(
    (consultant, connection) => {
      if (!UserState.ONLINE.equals(consultant.state)) {
        setActivePopup("callback");
        return;
      }

      if (connection.tariff > user.balanceAmount) {
        setActivePopup("no-credit");
        return;
      }

      if (connection.type !== "vas") {
        if (!isAuthenticated) {
          Router.push({
            pathname: "/register",
            query: {
              consultantName: consultant.displayName,
              consultantImage: consultant.picture,
              redirectTo: `/${consultant.slug}`,
            },
          });
          return;
        }
      } else {
        setActivePopup("znzz");
        return;
      }

      switch (connection.type) {
        case "video":
        case "audio":
          socket.emit(`${connection.type}.call`, { to: consultant.id });
          setActivePopup(connection.type);
          console.log(`SENT ${connection.type} CALL TO ${consultant.id}`);
          break;
        case "chat":
          onClose();
          pingToChat(consultant);
          break;
        case "phone":
          setActivePopup(user.phoneNumbers?.length > 0 ? "phone" : "add-phone-number");
          break;
        default:
          console.log(`UNKNOWN TYPE ${connection.type}`);
      }
    },
    [isAuthenticated, onClose, pingToChat, socket, user.balanceAmount, user.phoneNumbers?.length]
  );

  const activeComponent = useMemo(() => (activePopup ? POPUP_CLASSES[activePopup] : null), [activePopup]);

  return { activeComponent, call, setActivePopup, callType: activePopup };
}
